/**
 * Simple Session Management System
 * 
 * Reversed Flow Implementation:
 * - Normal Login: Data stored in both sessionStorage (with flag) and localStorage
 * - Impersonation: Data stored in localStorage only, no sessionStorage values
 * - Works across tabs while maintaining session integrity
 */

// Storage keys
const STORAGE_KEYS = {
  USER_SESSION: 'user_session',
  ADMIN_SESSION: 'admin_session', 
  IMPERSONATION_DATA: 'impersonation_data',
  NORMAL_LOGIN_FLAG: 'normal_login_active'
};

/**
 * Session Manager Class
 */
class SimpleSessionManager {
  constructor() {
    this.isImpersonating = this.checkImpersonationStatus();
  }

  /**
   * Check if currently in impersonation mode
   */
  checkImpersonationStatus() {
    try {
      // If there's no normal login flag in sessionStorage but data exists in localStorage,
      // we're likely in impersonation mode
      const normalLoginFlag = sessionStorage.getItem(STORAGE_KEYS.NORMAL_LOGIN_FLAG);
      const impersonationData = localStorage.getItem(STORAGE_KEYS.IMPERSONATION_DATA);
      
      return !normalLoginFlag && !!impersonationData;
    } catch (e) {
      return false;
    }
  }

  /**
   * Initialize normal login session
   * Stores data in both sessionStorage (with flag) and localStorage
   */
  initializeNormalSession(loginData, headerMenuData) {
    try {
      // Clear any existing impersonation data
      this.clearImpersonationData();

      const sessionData = {
        loginData,
        headerMenuData,
        timestamp: Date.now(),
        sessionType: 'normal'
      };

      // Store in sessionStorage with flag
      sessionStorage.setItem(STORAGE_KEYS.NORMAL_LOGIN_FLAG, 'true');
      sessionStorage.setItem(STORAGE_KEYS.USER_SESSION, JSON.stringify(sessionData));
      
      // Also store in localStorage for cross-tab persistence
      localStorage.setItem(STORAGE_KEYS.USER_SESSION, JSON.stringify(sessionData));

      this.isImpersonating = false;
      return true;
    } catch (e) {
      console.error('Failed to initialize normal session:', e);
      return false;
    }
  }

  /**
   * Initialize impersonation session
   * Stores data in localStorage only, clears sessionStorage
   */
  initializeImpersonationSession(impersonationData) {
    try {
      // Clear sessionStorage to remove normal login flag and data
      sessionStorage.removeItem(STORAGE_KEYS.NORMAL_LOGIN_FLAG);
      sessionStorage.removeItem(STORAGE_KEYS.USER_SESSION);
      sessionStorage.removeItem(STORAGE_KEYS.ADMIN_SESSION);

      const impersonationSessionData = {
        ...impersonationData,
        timestamp: Date.now(),
        sessionType: 'impersonation'
      };

      // Store impersonation data in localStorage only
      localStorage.setItem(STORAGE_KEYS.IMPERSONATION_DATA, JSON.stringify(impersonationSessionData));
      
      this.isImpersonating = true;
      return true;
    } catch (e) {
      console.error('Failed to initialize impersonation session:', e);
      return false;
    }
  }

  /**
   * Get current session data
   * Returns data based on current session type
   */
  getCurrentSession() {
    try {
      if (this.isImpersonating) {
        // Return impersonation data from localStorage
        const impersonationData = localStorage.getItem(STORAGE_KEYS.IMPERSONATION_DATA);
        return impersonationData ? JSON.parse(impersonationData) : null;
      } else {
        // Return normal session data (prefer sessionStorage, fallback to localStorage)
        const normalLoginFlag = sessionStorage.getItem(STORAGE_KEYS.NORMAL_LOGIN_FLAG);
        
        if (normalLoginFlag) {
          const sessionData = sessionStorage.getItem(STORAGE_KEYS.USER_SESSION);
          return sessionData ? JSON.parse(sessionData) : null;
        } else {
          // Fallback to localStorage if session data exists
          const localData = localStorage.getItem(STORAGE_KEYS.USER_SESSION);
          return localData ? JSON.parse(localData) : null;
        }
      }
    } catch (e) {
      console.error('Failed to get current session:', e);
      return null;
    }
  }

  /**
   * Store admin session for later restoration
   */
  storeAdminSession(adminData, headerMenuData) {
    try {
      const adminSessionData = {
        adminData,
        headerMenuData,
        timestamp: Date.now()
      };

      localStorage.setItem(STORAGE_KEYS.ADMIN_SESSION, JSON.stringify(adminSessionData));
      return true;
    } catch (e) {
      console.error('Failed to store admin session:', e);
      return false;
    }
  }

  /**
   * Get stored admin session
   */
  getAdminSession() {
    try {
      const adminData = localStorage.getItem(STORAGE_KEYS.ADMIN_SESSION);
      return adminData ? JSON.parse(adminData) : null;
    } catch (e) {
      console.error('Failed to get admin session:', e);
      return null;
    }
  }

  /**
   * Clear impersonation data and return to normal session
   */
  exitImpersonation() {
    try {
      // Clear impersonation data
      this.clearImpersonationData();

      // Check if normal session data exists in localStorage
      const normalSessionData = localStorage.getItem(STORAGE_KEYS.USER_SESSION);
      
      if (normalSessionData) {
        // Restore normal session flag in sessionStorage
        sessionStorage.setItem(STORAGE_KEYS.NORMAL_LOGIN_FLAG, 'true');
        sessionStorage.setItem(STORAGE_KEYS.USER_SESSION, normalSessionData);
      }

      this.isImpersonating = false;
      return true;
    } catch (e) {
      console.error('Failed to exit impersonation:', e);
      return false;
    }
  }

  /**
   * Clear impersonation data
   */
  clearImpersonationData() {
    try {
      localStorage.removeItem(STORAGE_KEYS.IMPERSONATION_DATA);
      return true;
    } catch (e) {
      console.error('Failed to clear impersonation data:', e);
      return false;
    }
  }

  /**
   * Clear all session data
   */
  clearAllSessions() {
    try {
      // Clear sessionStorage
      sessionStorage.removeItem(STORAGE_KEYS.NORMAL_LOGIN_FLAG);
      sessionStorage.removeItem(STORAGE_KEYS.USER_SESSION);
      sessionStorage.removeItem(STORAGE_KEYS.ADMIN_SESSION);

      // Clear localStorage
      localStorage.removeItem(STORAGE_KEYS.USER_SESSION);
      localStorage.removeItem(STORAGE_KEYS.ADMIN_SESSION);
      localStorage.removeItem(STORAGE_KEYS.IMPERSONATION_DATA);

      this.isImpersonating = false;
      return true;
    } catch (e) {
      console.error('Failed to clear all sessions:', e);
      return false;
    }
  }

  /**
   * Check if user has active session
   */
  hasActiveSession() {
    const currentSession = this.getCurrentSession();
    return !!currentSession;
  }

  /**
   * Get session type
   */
  getSessionType() {
    if (this.isImpersonating) {
      return 'impersonation';
    }
    
    const normalLoginFlag = sessionStorage.getItem(STORAGE_KEYS.NORMAL_LOGIN_FLAG);
    return normalLoginFlag ? 'normal' : 'none';
  }

  /**
   * Update current session data
   */
  updateCurrentSession(updates) {
    try {
      const currentSession = this.getCurrentSession();
      if (!currentSession) {
        return false;
      }

      const updatedSession = {
        ...currentSession,
        ...updates,
        timestamp: Date.now()
      };

      if (this.isImpersonating) {
        localStorage.setItem(STORAGE_KEYS.IMPERSONATION_DATA, JSON.stringify(updatedSession));
      } else {
        const normalLoginFlag = sessionStorage.getItem(STORAGE_KEYS.NORMAL_LOGIN_FLAG);
        
        if (normalLoginFlag) {
          sessionStorage.setItem(STORAGE_KEYS.USER_SESSION, JSON.stringify(updatedSession));
        }
        
        // Always update localStorage for normal sessions
        localStorage.setItem(STORAGE_KEYS.USER_SESSION, JSON.stringify(updatedSession));
      }

      return true;
    } catch (e) {
      console.error('Failed to update session:', e);
      return false;
    }
  }

  /**
   * Update header menu data for current session
   */
  updateHeaderMenuData(headerMenuData) {
    return this.updateCurrentSession({ headerMenuData });
  }

  /**
   * Setup cross-tab communication
   */
  setupCrossTabSync() {
    // Listen for storage changes to sync across tabs
    window.addEventListener('storage', (e) => {
      if (e.key === STORAGE_KEYS.IMPERSONATION_DATA && e.newValue) {
        // New impersonation started in another tab
        this.isImpersonating = true;
      } else if (e.key === STORAGE_KEYS.IMPERSONATION_DATA && !e.newValue) {
        // Impersonation ended in another tab
        this.isImpersonating = false;
      }
    });
  }

  /**
   * Check for existing impersonated tabs across the browser
   * Uses BroadcastChannel and localStorage to detect active tabs
   */
  async checkForExistingImpersonatedTabs() {
    return new Promise((resolve) => {
      try {
        // First check if there's impersonation data in localStorage
        const impersonationData = localStorage.getItem(STORAGE_KEYS.IMPERSONATION_DATA);
        if (!impersonationData) {
          resolve({ hasImpersonatedTabs: false, tabCount: 0 });
          return;
        }

        // Create a unique response channel for this check
        const responseChannelName = `tab-check-response-${Date.now()}-${Math.random()}`;
        const responseChannel = new BroadcastChannel(responseChannelName);
        const broadcastChannel = new BroadcastChannel('tab-impersonation-check');
        
        const responses = [];
        const timeout = 2000; // 2 second timeout
        
        // Listen for responses
        responseChannel.onmessage = (event) => {
          if (event.data.type === 'TAB_STATUS_RESPONSE') {
            responses.push({
              tabId: event.data.tabId,
              isImpersonated: event.data.isImpersonated,
              sessionType: event.data.sessionType,
              origin: event.data.origin
            });
          }
        };

        // Broadcast request to all tabs
        broadcastChannel.postMessage({
          type: 'TAB_STATUS_REQUEST',
          responseChannel: responseChannelName,
          requestId: Date.now()
        });

        // Wait for responses
        setTimeout(() => {
          broadcastChannel.close();
          responseChannel.close();
          
          const currentOrigin = window.location.origin;
          const sameOriginTabs = responses.filter(tab => tab.origin === currentOrigin);
          const impersonatedTabs = sameOriginTabs.filter(tab => tab.isImpersonated);
          
          resolve({
            hasImpersonatedTabs: impersonatedTabs.length > 0,
            tabCount: sameOriginTabs.length,
            impersonatedTabCount: impersonatedTabs.length,
            allTabs: responses
          });
        }, timeout);
        
      } catch (error) {
        console.error('Error checking for impersonated tabs:', error);
        resolve({ hasImpersonatedTabs: false, tabCount: 0, error: error.message });
      }
    });
  }

  /**
   * Setup tab status responder - responds to tab check requests
   */
  setupTabStatusResponder() {
    if (this.tabStatusChannel) {
      this.tabStatusChannel.close();
    }
    
    this.tabStatusChannel = new BroadcastChannel('tab-impersonation-check');
    this.tabStatusChannel.onmessage = (event) => {
      if (event.data.type === 'TAB_STATUS_REQUEST') {
        const responseChannel = new BroadcastChannel(event.data.responseChannel);
        
        // Send our tab status
        responseChannel.postMessage({
          type: 'TAB_STATUS_RESPONSE',
          tabId: this.getTabId(),
          isImpersonated: this.isImpersonating,
          sessionType: this.getSessionType(),
          origin: window.location.origin,
          timestamp: Date.now()
        });
        
        // Close the response channel after sending
        setTimeout(() => responseChannel.close(), 100);
      }
    };
  }

  /**
   * Generate a unique tab ID for this tab
   */
  getTabId() {
    if (!this.tabId) {
      this.tabId = `tab-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      // Store in sessionStorage so it persists for this tab only
      try {
        sessionStorage.setItem('tab_id', this.tabId);
      } catch (e) {
        // Handle storage errors
      }
    }
    return this.tabId;
  }

  /**
   * Cleanup tab status responder
   */
  cleanup() {
    if (this.tabStatusChannel) {
      this.tabStatusChannel.close();
      this.tabStatusChannel = null;
    }
  }
}

// Create singleton instance
const sessionManager = new SimpleSessionManager();

// Initialize cross-tab sync and tab status responder
sessionManager.setupCrossTabSync();
sessionManager.setupTabStatusResponder();

// Cleanup on page unload
window.addEventListener('beforeunload', () => {
  sessionManager.cleanup();
});

// Export the singleton instance
export default sessionManager;

// Export individual functions for convenience
export const {
  initializeNormalSession,
  initializeImpersonationSession,
  getCurrentSession,
  storeAdminSession,
  getAdminSession,
  exitImpersonation,
  clearAllSessions,
  hasActiveSession,
  getSessionType,
  updateCurrentSession,
  updateHeaderMenuData,
  checkImpersonationStatus,
  checkForExistingImpersonatedTabs
} = sessionManager;